// Typed wrapper for `GET /api/audit`.
//
// PR 7 scope (per openspec/changes/ui-redesign-v2/specs/dashboard-ui/spec.md,
// R-Audit-Page):
//
//   - `auditList({ action?, user? })` issues the REST call with the
//     right query string and returns the JSON array.
//
//   - `toAuditRow(view)` is a pure mapper that converts the wire
//     `AuditView` shape (snake_case Rust DTO from
//     `crates/agent/src/dto.rs::AuditView`) into the UI's `AuditRow`
//     shape with normalised field names. Centralised so the page
//     component never touches the wire DTO directly.
//
// Wire shape reminder (per `crates/agent/src/audit_view.rs::audit_handler`):
//
//   GET /api/audit?limit=200&action=restart&user=jack
//   → 200 [{ ts, user, action, target, result, ip, reason? }, ...]
//
// The orchestrator spec sketched a different shape
// (`{ entries: AuditEntry[] }` with `?since=&actor=&action=`), but
// "no backend changes" is a hard rule — the actual Rust agent returns
// a flat array with `user` / `ts` field names. We adapt the UI to
// the existing wire rather than refactor the agent.

import type { AuditView } from "./bindings";

export interface AuditFilters {
  /** Server-side action filter (e.g. "restart", "stop"). */
  action?: string;
  /** Server-side user/actor filter. */
  user?: string;
  /** Optional line cap; the backend defaults to 200. */
  limit?: number;
}

/** UI row shape — what the page renders. */
export interface AuditRow {
  /** Original ISO datetime string for display + sort. */
  tsIso: string;
  /** Parsed millis since epoch (Date.parse output). */
  timestampMs: number;
  /** User who initiated the action — wire `user`. */
  actor: string;
  /** Action verb — wire `action`. */
  action: string;
  /** Target container / unit / path. */
  target: string;
  /** UI-friendly result label ("ok" | "fail"). */
  result: "ok" | "fail";
  /** Visual tone for the row pill. */
  tone: "ok" | "fail";
  /** Free-form failure reason — empty string on success. */
  details: string;
  /** Originating IP — kept for debug surfacing only. */
  ip: string;
}

/**
 * Fetch one page of audit entries.
 *
 * The agent caps `limit` at 4096 (`MAX_LINES`) and returns the newest
 * N entries across the live file + rotated generations. We forward
 * the filter object as-is so callers can compose additional fields
 * (e.g. `since=…`) without breaking the wrapper.
 */
export async function auditList(filters: AuditFilters): Promise<AuditView[]> {
  const url = new URL("/api/audit", window.location.href);
  if (filters.action) url.searchParams.set("action", filters.action);
  if (filters.user) url.searchParams.set("user", filters.user);
  if (typeof filters.limit === "number") {
    url.searchParams.set("limit", String(filters.limit));
  }

  const res = await window.fetch(url.pathname + url.search, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`audit fetch failed: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as AuditView[];
}

/**
 * Pure mapper — convert one wire `AuditView` into the UI row shape.
 *
 * The mapper is split from the page so it can be unit-tested without
 * rendering Svelte components. Behaviour:
 *
 *   - `result: "ok"      → tone "ok",  result "ok"`.
 *   - `result: anything  → tone "fail", result "fail"` (the wire uses
 *     `"error"` but the UI label is `"fail"` — same shape, friendlier
 *     label).
 *   - `reason: undefined → details ""`; any string passes through
 *     unchanged so the row can expand to show the failure excerpt.
 *   - `ts: "..."         → tsIso` + `timestampMs` via `Date.parse`.
 *     The page renders `tsIso` directly; the parser exists so a
 *     future sort-by-time helper can compare numerically without
 *     re-parsing.
 */
export function toAuditRow(view: AuditView): AuditRow {
  const isOk = view.result === "ok";
  const timestampMs = Date.parse(view.ts);
  return {
    tsIso: view.ts,
    timestampMs: Number.isFinite(timestampMs) ? timestampMs : 0,
    actor: view.user,
    action: view.action,
    target: view.target,
    result: isOk ? "ok" : "fail",
    tone: isOk ? "ok" : "fail",
    details: view.reason ?? "",
    ip: view.ip,
  };
}
